import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

export interface CardCover {
  src: string;
  alt?: string;
  aspectRatio?: string; // e.g. '16/9', '1/1', '4/3'
}

export interface CardTargetLink {
  href: string;
  external?: boolean;
}

export interface CardFieldBase {
  id: string;
  label?: string;
  visible?: boolean;
  style?: 'title' | 'description' | 'default';
}

export interface CardTextField extends CardFieldBase {
  type: 'text';
  value: string;
}

export interface CardFileItem {
  src: string;
  name?: string;
  mime?: string;
}

export interface CardFilesField extends CardFieldBase {
  type: 'files';
  files: CardFileItem[];
}

export interface CardLinkField extends CardFieldBase {
  type: 'link';
  href: string;
  text?: string;
  external?: boolean;
}

export type CardField = CardTextField | CardFilesField | CardLinkField;

export interface CardAttrs {
  id: string;
  cover: CardCover | null;
  targetLink: CardTargetLink | null;
  fields: CardField[];
}

export interface CardOptions {
  HTMLAttributes: Record<string, any>;
  view: any;
}

function parseJsonAttr<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export const Card = Node.create<CardOptions>({
  name: 'card',
  group: 'card',
  atom: true,
  selectable: true,
  draggable: true,
  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      view: null,
    };
  },

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-id') || '',
        renderHTML: (attrs) => ({ 'data-id': attrs.id ?? '' }),
      },
      cover: {
        default: null,
        parseHTML: (el) =>
          parseJsonAttr<CardCover | null>(el.getAttribute('data-cover'), null),
        renderHTML: (attrs) =>
          attrs.cover ? { 'data-cover': JSON.stringify(attrs.cover) } : {},
      },
      targetLink: {
        default: null,
        parseHTML: (el) =>
          parseJsonAttr<CardTargetLink | null>(
            el.getAttribute('data-target-link'),
            null,
          ),
        renderHTML: (attrs) =>
          attrs.targetLink
            ? { 'data-target-link': JSON.stringify(attrs.targetLink) }
            : {},
      },
      fields: {
        default: [],
        parseHTML: (el) =>
          parseJsonAttr<CardField[]>(el.getAttribute('data-fields'), []),
        renderHTML: (attrs) => ({
          'data-fields': JSON.stringify(attrs.fields ?? []),
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="card"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
    ];
  },

  addNodeView() {
    if (!this.options.view) return null;
    return ReactNodeViewRenderer(this.options.view);
  },
});
